// 📁 pages/Profile.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import axios from '../utils/axiosInstance';

const Profile = ({ setIsAuthenticated, userRole, userEmail }) => {
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSidebarVisible, setSidebarVisible] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024;
    }
    return true;
  });
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setSidebarVisible(!isSidebarVisible);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
    navigate('/');
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setIsAuthenticated(false);
      navigate('/');
      return;
    }

    const fetchProfile = async () => {
      try {
        const response = await axios.get('/profile');
        setEmail(response.data.email);
      } catch (err) {
        setError('Не удалось загрузить информацию о профиле.');
        setEmail('');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate, setIsAuthenticated]);

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Пароли не совпадают.');
      return;
    }

    try {
      await axios.put('/update-password', { currentPassword, newPassword });
      setSuccess('Пароль успешно обновлён.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      setTimeout(() => setSuccess(''), 3000); // автоочистка уведомления
    } catch (err) {
      // Обрабатываем ошибку обновления пароля
      if (err.response?.status === 401) {
        // 401 означает неверный текущий пароль
        setError(err.response?.data?.message || 'Текущий пароль неверен. Проверьте правильность ввода.');
      } else if (err.response?.status === 400) {
        // 400 означает проблемы с валидацией (например, короткий пароль)
        setError(err.response?.data?.message || 'Пароль не соответствует требованиям.');
      } else {
        // Другие ошибки
        setError(err.response?.data?.message || 'Ошибка при обновлении пароля. Попробуйте позже.');
      }
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-surface-base text-muted-100">
      <Header 
        isSidebarVisible={isSidebarVisible} 
        toggleSidebar={toggleSidebar} 
        userEmail={email || userEmail}
        userRole={userRole}
        onLogout={handleLogout}
      />
      <div className="flex flex-1">
        <Sidebar 
          onLogout={handleLogout} 
          userRole={userRole} 
          isSidebarVisible={isSidebarVisible}
          onClose={() => setSidebarVisible(false)}
        />
        <main className="relative flex min-w-0 flex-1 flex-col gap-6 bg-surface-base/60 px-4 py-6 transition-all duration-300 ease-brand sm:px-6 lg:px-10">
          <div className="container mx-auto max-w-5xl">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-white mb-2 font-display">Мой профиль</h1>
              <p className="text-muted-300">Управление настройками аккаунта и безопасностью</p>
            </div>

            {/* Alerts */}
            {error && (
              <div className="mb-6 rounded-2.5xl border border-critical/50 bg-critical/10 p-4 flex items-center gap-3 animate-in slide-in-from-top-2">
                <span className="material-icons text-critical text-xl">error_outline</span>
                <p className="text-critical flex-1">{error}</p>
                <button
                  onClick={() => setError('')}
                  className="text-critical hover:text-critical/70 transition"
                >
                  <span className="material-icons">close</span>
                </button>
              </div>
            )}

            {success && (
              <div className="mb-6 rounded-2.5xl border border-positive/50 bg-positive/10 p-4 flex items-center gap-3 animate-in slide-in-from-top-2">
                <span className="material-icons text-positive text-xl">check_circle</span>
                <p className="text-positive flex-1">{success}</p>
                <button
                  onClick={() => setSuccess('')}
                  className="text-positive hover:text-positive/70 transition"
                >
                  <span className="material-icons">close</span>
                </button>
              </div>
            )}

            {/* Profile Info Card */}
            <div className="mb-6 rounded-3xl border border-surface-border bg-surface-raised/60 backdrop-blur-xl p-6 shadow-elevated">
              <div className="flex items-center gap-3 mb-6">
                <span className="material-icons text-brand-400 text-2xl">account_circle</span>
                <h2 className="text-2xl font-semibold text-white font-display">Информация о профиле</h2>
              </div>
              
              {loading ? (
                <div className="flex items-center gap-3 text-muted-400">
                  <span className="material-icons animate-spin">hourglass_empty</span>
                  <span>Загрузка информации...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 rounded-2xl bg-surface-subtle/40 border border-surface-border/30">
                    <span className="material-icons text-brand-300 text-2xl">email</span>
                    <div className="flex-1">
                      <p className="text-xs uppercase tracking-[0.3em] text-muted-500 mb-1">Email адрес</p>
                      <p className="text-lg font-semibold text-white">{email || 'Недоступно'}</p>
                    </div>
                  </div>
                  
                  {userRole && (
                    <div className="flex items-center gap-4 p-4 rounded-2xl bg-surface-subtle/40 border border-surface-border/30">
                      <span className="material-icons text-brand-300 text-2xl">badge</span>
                      <div className="flex-1">
                        <p className="text-xs uppercase tracking-[0.3em] text-muted-500 mb-1">Роль</p>
                        <p className="text-lg font-semibold text-white">
                          {userRole === 'admin' ? 'Администратор' :
                           userRole === 'engineer' ? 'Инженер' :
                           userRole === 'viewer' ? 'Просмотр' :
                           userRole === 'demo' ? 'Демо' : userRole}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Password Update Card */}
            <div className="rounded-3xl border border-surface-border bg-surface-raised/60 backdrop-blur-xl p-6 shadow-elevated">
              <div className="flex items-center gap-3 mb-6">
                <span className="material-icons text-brand-400 text-2xl">lock</span>
                <h2 className="text-2xl font-semibold text-white font-display">Обновить пароль</h2>
              </div>

              <form onSubmit={handlePasswordUpdate} className="space-y-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-muted-300">
                    Текущий пароль
                  </label>
                  <div className="relative">
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Введите текущий пароль"
                      className="w-full px-4 py-3 rounded-2xl border border-surface-border bg-surface-subtle/40 text-white placeholder-muted-400 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent transition"
                      required
                    />
                    <span className="material-icons absolute right-3 top-1/2 -translate-y-1/2 text-muted-400 text-lg">lock</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-muted-300">
                    Новый пароль
                  </label>
                  <div className="relative">
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Введите новый пароль"
                      className="w-full px-4 py-3 rounded-2xl border border-surface-border bg-surface-subtle/40 text-white placeholder-muted-400 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent transition"
                      required
                    />
                    <span className="material-icons absolute right-3 top-1/2 -translate-y-1/2 text-muted-400 text-lg">vpn_key</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-muted-300">
                    Подтвердите новый пароль
                  </label>
                  <div className="relative">
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Повторите новый пароль"
                      className="w-full px-4 py-3 rounded-2xl border border-surface-border bg-surface-subtle/40 text-white placeholder-muted-400 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent transition"
                      required
                    />
                    <span className="material-icons absolute right-3 top-1/2 -translate-y-1/2 text-muted-400 text-lg">check_circle</span>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl bg-brand-linear px-6 py-3 text-sm font-semibold text-white shadow-brand transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 focus:ring-offset-surface-raised"
                >
                  <span className="material-icons text-lg">save</span>
                  Обновить пароль
                </button>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Profile;
